import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Sparkles, X } from 'lucide-react';
import { InsightItem } from '../../services/insights';
import { EVENTO_ABRIR_ASSISTENTE, useInsights } from './InsightsProvider';

/** Espera depois de abrir a tela, para o balão não disputar atenção com o carregamento. */
const ESPERA_MS = 6000;

const entrar = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Caixa = styled.div`
  position: fixed;
  right: 24px;
  bottom: 96px;
  width: 340px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
  padding: 16px;
  box-sizing: border-box;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${entrar} 0.25s ease-out;

  @media (max-width: 480px) {
    right: 16px;
    bottom: 84px;
  }
`;

const Topo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1f467e;
`;

const Fechar = styled.button`
  margin-left: auto;
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Titulo = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: #1f2937;
`;

const Texto = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: #475569;
`;

const Acoes = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const Principal = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: #1f467e;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
`;

const Secundario = styled.button`
  height: 34px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 13px;
  cursor: pointer;
`;

const VerTodos = styled.button`
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #1f467e;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const CHAMADAS: Record<string, string> = {
  aviso: 'Notei uma coisa importante',
  acao: 'Tenho uma sugestão para você',
  dica: 'Uma dica para a sua locadora',
};

/**
 * O balão proativo: um insight por dia, que aparece sozinho ao lado do avatar. Não aparece no Dashboard (lá
 * o card já faz esse papel) nem durante tours e guias.
 */
export const BalaoProativo: React.FC<{ oculto?: boolean }> = ({ oculto }) => {
  const { habilitado, balao, itens, marcarBalaoMostrado, interagir } = useInsights();
  const location = useLocation();
  const history = useHistory();
  const [visivel, setVisivel] = useState<InsightItem | null>(null);

  const podeMostrar = habilitado && !!balao && !oculto && location.pathname !== '/home';

  useEffect(() => {
    if (!podeMostrar || visivel || !balao) return undefined;
    const item = balao;
    const temporizador = window.setTimeout(() => {
      setVisivel(item);
      marcarBalaoMostrado(item);
    }, ESPERA_MS);
    return () => window.clearTimeout(temporizador);
  }, [podeMostrar, balao, visivel, marcarBalaoMostrado]);

  if (!visivel || oculto) return null;

  const fechar = () => setVisivel(null);
  const abrirAcao = () => {
    interagir(visivel, 'cta', 'balao', 0);
    if (visivel.linkCta) history.push(visivel.linkCta);
    fechar();
  };
  const depois = () => {
    interagir(visivel, 'depois', 'balao', 0);
    fechar();
  };
  const verTodos = () => {
    window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_ASSISTENTE, { detail: { aba: 'insights' } }));
    fechar();
  };

  return (
    <Caixa role="status" aria-live="polite">
      <Topo>
        <Sparkles size={16} />
        {CHAMADAS[visivel.tipo] ?? CHAMADAS.dica}
        <Fechar type="button" aria-label="Fechar" onClick={fechar}><X size={16} /></Fechar>
      </Topo>
      <Titulo>{visivel.titulo}</Titulo>
      {visivel.texto && <Texto>{visivel.texto}</Texto>}
      <Acoes>
        {visivel.linkCta && <Principal type="button" onClick={abrirAcao}>{visivel.textoCta || 'Ver'}</Principal>}
        <Secundario type="button" onClick={depois}>Depois</Secundario>
        {itens.length > 1 && <VerTodos type="button" onClick={verTodos}>Ver todos ({itens.length})</VerTodos>}
      </Acoes>
    </Caixa>
  );
};
